import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment, ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useMenuDrawerContext } from "../menu/MenuDrawerContextProvider.shared";

const drawerRoot = document.getElementById("drawer");

export type DrawerProps = {
  sticky: boolean;
  active: boolean;
  setActive: (active: boolean) => void;
  children: ReactNode;
  position: "left" | "right";
  className?: string;
};
export function Drawer(props: DrawerProps): ReactElement {
  const { displayTypeOverride } = useMenuDrawerContext();
  const active = props.active && displayTypeOverride !== "hidden";
  const positionClassName =
    props.position === "right"
      ? "right-0 w-[350px] border-l"
      : "left-0 w-[300px] border-r";
  const panelClassName = twMerge(
    `fixed ${zLayer.drawer} flex max-w-[80vw] flex-col overflow-x-hidden overflow-y-auto overscroll-contain border-solid border-gray-300 bg-white transition duration-200 ease-out`,
    props.sticky
      ? "top-[calc(var(--refresh-pwa-banner-height)+var(--hidden-platform-banner-height))] bottom-0"
      : "inset-y-0",
    positionClassName,
    props.className,
  );

  if (props.sticky) {
    if (!drawerRoot) throw new Error("Cannot find drawer element.");

    return createPortal(
      <div
        className={twMerge(
          panelClassName,
          active
            ? "translate-x-0"
            : props.position === "right"
              ? "translate-x-full"
              : "-translate-x-full",
        )}
      >
        <FloatingLayerProvider host="drawer">
          {props.children}
        </FloatingLayerProvider>
      </div>,
      drawerRoot,
    );
  }

  return (
    <Transition show={active} as={Fragment}>
      <Dialog
        onClose={() => props.setActive(false)}
        className={`relative ${zLayer.drawer}`}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/60" />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom={
            props.position === "right"
              ? "translate-x-full"
              : "-translate-x-full"
          }
          enterTo="translate-x-0"
          leave="ease-in duration-200"
          leaveFrom="translate-x-0"
          leaveTo={
            props.position === "right"
              ? "translate-x-full"
              : "-translate-x-full"
          }
        >
          <DialogPanel className={panelClassName}>
            <FloatingLayerProvider host="drawer">
              {props.children}
            </FloatingLayerProvider>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}
